/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { format, isValid, parseISO } from "date-fns";
import _ from "lodash";
import { IonSpinner } from "@ionic/react";
import classNames from "classnames";
import { EvidenceAttachment } from "@interfaces/Interfaces";
import DOMPurify from "dompurify";

interface LegacyEvidenceRendererProps {
  viewingAttachment: boolean;
  viewingAttachmentFilename: string;
  onViewAttachment: (attachment: EvidenceAttachment) => void;
}

export const useLegacyEvidenceRenderer = ({
  viewingAttachment,
  viewingAttachmentFilename,
  onViewAttachment,
}: LegacyEvidenceRendererProps) => {
  const sanitizeData = (data: string) => {
    return { __html: DOMPurify.sanitize(data) };
  };

  const getLegacyEvidencePartType = (value: any) => {
    if (typeof value === "string") {
      if (/^\d{4}-\d{2}-\d{2}/.test(value) && isValid(parseISO(value))) {
        return "date";
      }
      return "text";
    }

    if (Array.isArray(value)) {
      if (value[0]?.Name) {
        return "LOs";
      }
      if (value[0]?.skillId) {
        return "skills";
      }
      if (typeof value[0] === "string") {
        return "tickbox-list";
      }
      if (value[0]?.displayName) {
        return "LOs-display-name";
      }
      return "array";
    }

    if (typeof value === "object") {
      if (_.isEmpty(value)) {
        return "empty-object";
      }
      if (value?.selectedOutcomes) {
        return "NQP-LOs";
      }
      if (value?.attachments) {
        return "attachments";
      }
      if (value?.radioValue || value?.textValue) {
        return "radio-text";
      }
      if (value?.name && value?.email) {
        return "mentor-info";
      }
      if (value?.label) {
        return "label";
      }
      return "object";
    }

    return "unknown";
  };

  const renderLegacyEvidencePart = (type: string, value: any) => {
    if (type === "text") {
      return (
        <div
          className="readEvidenceText"
          dangerouslySetInnerHTML={sanitizeData(value)}
        />
      );
    }

    if (type === "date") {
      return <div className="readEvidenceText">{format(new Date(value), "d MMM yyyy")}</div>;
    }

    if (type === "LOs") {
      return <div className="readEvidenceText">{value.map((lo: any) => lo.Name).join(", ")}</div>;
    }

    if (type === "LOs-display-name") {
      return <div className="readEvidenceText">{value.map((lo: any) => lo.displayName).join(", ")}</div>;
    }

    if (type === "NQP-LOs") {
      return (
        <div className="readEvidenceText">
          {value.selectedOutcomes.map((lo: any, index: number) => (
            <div
              key={lo.id}
              className={`${index !== value.selectedOutcomes.length - 1 ? "mb-1" : ""}`}
            >
              <div className="readEvidenceText">{lo.name}</div>
            </div>
          ))}
        </div>
      );
    }

    if (type === "tickbox-list") {
      return <div className="readEvidenceText">{Array.isArray(value) ? [...value].sort().join(", ") : value}</div>;
    }

    if (type === "skills") {
      const groupedData = _.groupBy(value ?? [], (skill) => skill.topSection);
      const mappedData = Object.keys(groupedData)
        .sort()
        .map((key) => {
          return _.groupBy(
            groupedData[key]!.sort((a, b) => a.topSection!.localeCompare(b.topSection!)),
            (item) => item.topSection!
          );
        });

      return (
        <div>
          {mappedData.map((item) => {
            const topSectionKey = Object.keys(item)[0];
            const skillsArray = item[topSectionKey];

            return (
              <div key={topSectionKey}>
                <div className="readEvidenceHeader">{topSectionKey}</div>
                <div className="mt-2">
                  {skillsArray?.map((skill) => (
                    <div
                      key={skill.skillId}
                      className="readEvidenceText"
                    >
                      {skill.skillName}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    if (type === "attachments") {
      return (
        <div className="readEvidenceText">
          {value.attachments?.map((attachment: EvidenceAttachment) => {
            const disabled = viewingAttachment && viewingAttachmentFilename !== attachment.name;
            const viewing = viewingAttachment && viewingAttachmentFilename === attachment.name;

            return (
              <div
                key={attachment.name}
                id={attachment.name}
                className={classNames("readEvidenceAttachmentContainer", {
                  readEvidenceAttachmentContainerWait: viewing,
                  readEvidenceAttachmentContainerDisabled: disabled,
                })}
                onClick={() => !(disabled || viewing) && onViewAttachment(attachment)}
              >
                <div className="readEvidenceAttachment">{attachment.name}</div>
                {viewing && (
                  <div className="readEvidenceSpinnerContainer">
                    <IonSpinner className="readEvidenceAttachmentIconSpinner" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    if (type === "empty-object") {
      return <div className="readEvidenceText">{"No data"}</div>;
    }

    if (type === "object") {
      return <div className="readEvidenceText">{JSON.stringify(value, null, 2)}</div>;
    }

    if (type === "radio-text") {
      return (
        <>
          <div className="readEvidenceText">{value.radioValue}</div>
          {value.textValue && (
            <>
              <div className="readEvidenceHeader mt-2">{"Comments"}</div>
              <div
                className="readEvidenceText"
                dangerouslySetInnerHTML={sanitizeData(value.textValue)}
              />
            </>
          )}
        </>
      );
    }

    if (type === "mentor-info") {
      return (
        <>
          <div className="readEvidenceHeader mt-2">{"Mentor Name:"}</div>
          <div className="readEvidenceText">{value.name ?? "N/A"}</div>
          <div className="readEvidenceHeader mt-2">{"Mentor Email:"}</div>
          <div className="readEvidenceText">{value.email ?? "N/A"}</div>
        </>
      );
    }

    if (type === "label") {
      return <div className="readEvidenceText">{value.label}</div>;
    }

    return null;
  };

  return {
    getLegacyEvidencePartType,
    renderLegacyEvidencePart,
  };
};

// For backward compatibility
export const LegacyEvidenceRenderer: React.FC<LegacyEvidenceRendererProps> = () => {
  // This component doesn't render anything, it's just a provider for the hook
  return null;
};
